import {
  IconText,
  MuiTextfield,
  noForwardProps,
  useGetPublicAccountDetail,
  UserSVG
} from '@apeiron/temp'
import { useRoninNameService, useWalletAddress } from '@apeiron/wallet'
import { Box, styled, TextFieldProps, Typography } from '@mui/material'
import { INPUT_DEBOUNCE } from '@src/constants/layout'
import * as R from 'ramda'
import {
  ChangeEvent,
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useTranslation } from 'react-i18next'
import { useDebouncedCallback } from 'use-debounce'
import { isAddress } from 'viem'

const StyledTextfield = styled(MuiTextfield, noForwardProps)<{
  __isValidAddress: boolean
}>`
  .MuiOutlinedInput-root {
    height: 36px;
    padding: 0px 14px;
    background-color: black;
    color: ${props =>
      !props.value || props.__isValidAddress ? 'white' : '#ff0000'};
    ${props =>
      !R.isEmpty(props.value) && !props.__isValidAddress && 'font-weight: 700;'}
  }
  .MuiOutlinedInput-input {
    background-color: unset;
    box-shadow: unset;
    font-size: 14px;
    line-height: 14px;
    padding: 0px;

    &.Mui-disabled {
      -webkit-text-fill-color: ${props =>
        props.__isValidAddress ? 'white' : '#ff0000'};
    }
  }

  :hover .MuiOutlinedInput-notchedOutline,
  .MuiOutlinedInput-notchedOutline {
    border-color: #161616;
    border-width: 1px;
    border-radius: 2px;
  }
  .Mui-focused .MuiOutlinedInput-notchedOutline {
    border-color: #ffbf36;
    border-width: 1px;
  }
`

const EndAdornment = styled(Box)`
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: flex-start;
  max-width: 55%;
`

const Slash = styled(Typography)`
  color: #ffaa03;
  margin: 10px;
  font-size: 20px;
  font-weight: 500;
  line-height: 20px;
`

const UserName = styled(IconText)`
  flex: 1;
  overflow: hidden;
  .Custom-Icon {
    filter: brightness(70%);
    min-width: 20px;
    width: 20px;
  }
  .Custom-Text {
    color: #c4c5c5;
    font-size: 14px;
    font-weight: 300;
    line-height: 14px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

const Error = styled(Typography)`
  color: red;
  font-size: 14px;
  font-weight: 500;
  line-height: 14px;
  white-space: nowrap;
`

const Loading = styled(Typography)`
  color: #c4c5c5;
  font-size: 14px;
  font-weight: 300;
  line-height: 14px;
  white-space: nowrap;
`

const ApeironWalletAddressInput: FC<Props> = (props: Props) => {
  const {
    allowUnknownUser = false,
    value = '',
    onChangeAddress,
    onCheckAddress,
    ...rest
  } = props

  const [loading, setLoading] = useState<boolean>(false)

  const [fetched, setFetched] = useState<boolean>(false)

  const [input, setInput] = useState<string>('')

  const { t } = useTranslation()

  const { data: user, fetch: fetchAccount } = useGetPublicAccountDetail()

  const { maybeAddress, toEthAddress } = useWalletAddress()

  const { maybeRNS, resolveName } = useRoninNameService()

  const handleOnCheckAccount = useCallback(
    async (address: string) => {
      if (isAddress(address)) {
        setLoading(true)

        const account = await fetchAccount({ walletAddress: address })

        setFetched(true)
        setLoading(false)

        if (onCheckAddress) {
          onCheckAddress(allowUnknownUser || !R.isNil(account))
        }
      } else {
        setFetched(false)

        if (onCheckAddress) {
          onCheckAddress(false)
        }
      }
    },
    [allowUnknownUser, fetchAccount, onCheckAddress]
  )

  const handleOnCheck = useCallback(
    async (input: string) => {
      if (maybeAddress(input)) {
        const address = toEthAddress(input)

        await handleOnCheckAccount(address)

        if (onChangeAddress) {
          onChangeAddress(address)
        }

        return
      }

      if (maybeRNS(input)) {
        setLoading(true)

        const address = await resolveName(input)

        setInput(
          `${input} (${address || t('wallet_address.input.rns_not_bound')})`
        )

        await handleOnCheckAccount(address)

        if (onChangeAddress) {
          onChangeAddress(address)
        }

        return
      }

      setFetched(false)

      if (onChangeAddress) {
        onChangeAddress('')
      }
    },
    [
      handleOnCheckAccount,
      maybeAddress,
      maybeRNS,
      onChangeAddress,
      resolveName,
      t,
      toEthAddress
    ]
  )

  const handleOnDebounceCheck = useDebouncedCallback(
    handleOnCheck,
    INPUT_DEBOUNCE
  )

  const handleOnInput = useCallback(
    async (input: string) => {
      setInput(input)
      await handleOnDebounceCheck(input)
    },
    [handleOnDebounceCheck]
  )

  const handleOnChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const input = event.target.value

      await handleOnInput(input)
    },
    [handleOnInput]
  )

  useEffect(() => {
    handleOnInput(value)
  }, [value])

  const Adornment = useMemo(() => {
    if (loading) {
      return (
        <EndAdornment>
          <Slash>/</Slash>
          <Loading>{t('wallet_address.input.loading')}</Loading>
        </EndAdornment>
      )
    }

    if (fetched && R.isNil(user)) {
      return (
        <EndAdornment>
          <Slash>/</Slash>
          {allowUnknownUser ? (
            <UserName
              className='Custom-User-Name'
              startSrc={''}
              text={t('wallet_address.input.no_user')}
            />
          ) : (
            <Error>{t('wallet_address.input.invalid')}</Error>
          )}
        </EndAdornment>
      )
    }

    if (fetched && !R.isNil(user)) {
      return (
        <EndAdornment>
          <Slash>/</Slash>
          <UserName
            className='Custom-User-Name'
            startSrc={UserSVG}
            text={`${user?.name} #${user?.tag}`}
          />
        </EndAdornment>
      )
    }

    return undefined
  }, [allowUnknownUser, fetched, loading, t, user])

  return (
    <StyledTextfield
      __isValidAddress={fetched}
      autoComplete='off'
      className='Custom-Textfield'
      InputProps={{ endAdornment: Adornment }}
      onChange={handleOnChange}
      {...rest}
      value={input}
    />
  )
}

type Props = TextFieldProps & {
  allowUnknownUser?: boolean
  value?: string
  onChangeAddress?: (address: string) => void
  onCheckAddress?: (valid: boolean) => void
}

export default ApeironWalletAddressInput
